import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as Dialog from "@radix-ui/react-dialog";
import { Search, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState, } from "react";
import { Button } from "./Button.js";
import { cn } from "./cn.js";
import { Input } from "./Input.js";
/**
 * Global-search dialog: type to filter a flat list of destinations, pick one with
 * click OR keyboard (ArrowDown/ArrowUp to move the highlight, Enter to navigate,
 * Escape closes the dialog itself). The input follows the combobox pattern — the
 * listbox is a sibling it references by id, and `aria-activedescendant` points at
 * the highlighted option so focus never has to leave the field while arrowing.
 */
export function CommandDialog({ open, onOpenChange, title, placeholder, emptyLabel, items, }) {
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const listboxId = useId();
    const listRef = useRef(null);
    const normalized = query.trim().toLowerCase();
    const results = useMemo(() => {
        if (!normalized)
            return items;
        return items.filter((item) => [item.label, item.description, ...(item.keywords ?? [])]
            .filter(Boolean)
            .some((value) => value?.toLowerCase().includes(normalized)));
    }, [items, normalized]);
    // A fresh dialog opens on an empty query, and any query/highlight is stale the
    // moment the filtered list shrinks — clamp or clear both here.
    useEffect(() => {
        if (open) {
            setQuery("");
            setActiveIndex(0);
        }
    }, [open]);
    useEffect(() => {
        setActiveIndex((index) => Math.min(index, Math.max(0, results.length - 1)));
    }, [results.length]);
    useEffect(() => {
        listRef.current
            ?.querySelector('[aria-selected="true"]')
            ?.scrollIntoView({ block: "nearest" });
    }, [activeIndex]);
    const select = (item) => {
        item.onSelect();
        onOpenChange(false);
        setQuery("");
    };
    const onKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            if (results.length > 0)
                setActiveIndex((index) => (index + 1) % results.length);
        }
        else if (event.key === "ArrowUp") {
            event.preventDefault();
            if (results.length > 0) {
                setActiveIndex((index) => (index - 1 + results.length) % results.length);
            }
        }
        else if (event.key === "Enter") {
            event.preventDefault();
            const item = results[activeIndex];
            if (item)
                select(item);
        }
    };
    return (_jsx(Dialog.Root, { open: open, onOpenChange: onOpenChange, children: _jsxs(Dialog.Portal, { children: [_jsx(Dialog.Overlay, { className: "fixed inset-0 z-40 bg-background/70 backdrop-blur-sm" }), _jsxs(Dialog.Content, { className: "fixed left-1/2 top-[12vh] z-50 w-[min(42rem,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-card shadow-lg focus-visible:outline-none", children: [_jsxs("div", { className: "flex items-center gap-3 border-border border-b px-4 py-3", children: [_jsx(Search, { className: "size-5 shrink-0 text-muted-foreground", "aria-hidden": "true" }), _jsx(Dialog.Title, { className: "sr-only", children: title }), _jsx(Input, { autoFocus: true, role: "combobox", "aria-expanded": true, "aria-controls": listboxId, "aria-activedescendant": results[activeIndex]
                                        ? `${listboxId}-opt-${results[activeIndex].id}`
                                        : undefined, value: query, onChange: (event) => setQuery(event.target.value), onKeyDown: onKeyDown, placeholder: placeholder, 
                                    /*
                                     * `ring-0` alone was not enough. Tailwind treats ring width and ring
                                     * OFFSET as separate utilities, so the base Input's `ring-offset-1`
                                     * survived the override — and v4 initialises `--tw-ring-offset-color`
                                     * to `#fff`, so the field wore a 1px WHITE box on the dark palette
                                     * (measured: `rgb(255,255,255) 0 0 0 1px` over `rgb(24,24,27)`). The
                                     * field is deliberately chrome-less here: the dialog is the focus
                                     * context and the highlighted option is the visible focus.
                                     */
                                    className: "h-10 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0" }), _jsx(Dialog.Close, { asChild: true, children: _jsx(Button, { size: "icon", variant: "ghost", "aria-label": "Close", children: _jsx(X, { "aria-hidden": "true" }) }) })] }), _jsx("div", { className: "max-h-[60vh] overflow-y-auto p-2", children: results.length === 0 ? (_jsx("p", { className: "px-3 py-8 text-center text-muted-foreground text-sm", children: emptyLabel })) : (_jsx("ul", { ref: listRef, id: listboxId, role: "listbox", "aria-label": title, className: "grid gap-1", children: results.map((item, index) => (_jsx("li", { role: "presentation", children: _jsxs("button", { type: "button", role: "option", id: `${listboxId}-opt-${item.id}`, "aria-selected": index === activeIndex, onClick: () => select(item), onMouseMove: () => setActiveIndex(index), className: cn("flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors", index === activeIndex
                                            ? "bg-secondary"
                                            : "hover:bg-secondary focus-visible:bg-secondary", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"), children: [item.icon ? (_jsx("span", { className: "text-muted-foreground [&_svg]:size-4", "aria-hidden": "true", children: item.icon })) : null, _jsxs("span", { className: "min-w-0 flex-1", children: [_jsx("span", { className: "block font-medium text-foreground text-sm", children: item.label }), item.description ? (_jsx("span", { className: "block truncate text-muted-foreground text-xs", children: item.description })) : null] })] }) }, item.id))) })) })] })] }) }));
}
